import React, { useEffect, useMemo, useState } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, Text } from '../../components/ui'
import type { MarketingRequest } from '../../types/marketingSolicitacao'
import { formatRequestSelectLabel } from './notasUi'
import {
  SELECT_CLASS,
  formatRequestNumber,
  formatRequesterWithDepartment,
} from './solicitacaoUi'

type Props = {
  open: boolean
  requests: MarketingRequest[]
  value: string
  onClose: () => void
  onSelect: (requestId: string) => void
}

const SolicitacaoSelectModal: React.FC<Props> = ({
  open,
  requests,
  value,
  onClose,
  onSelect,
}) => {
  const [search, setSearch] = useState('')
  const [requesterFilter, setRequesterFilter] = useState('')
  const [pendingId, setPendingId] = useState(value)

  useEffect(() => {
    if (!open) return
    setPendingId(value)
    setSearch('')
    setRequesterFilter('')
  }, [open, value])

  const requesterOptions = useMemo(() => {
    const byId = new Map<string, MarketingRequest['requester']>()
    for (const row of requests) {
      if (!row.requester_id || !row.requester) continue
      if (!byId.has(row.requester_id)) byId.set(row.requester_id, row.requester)
    }
    return Array.from(byId.entries())
      .map(([id, requester]) => ({
        id,
        label: formatRequesterWithDepartment(requester),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
  }, [requests])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return requests.filter((row) => {
      if (requesterFilter && row.requester_id !== requesterFilter) return false
      if (!term) return true
      const numberMatch =
        String(row.request_number).includes(term.replace(/^#/, '')) ||
        formatRequestNumber(row.request_number).toLowerCase().includes(term)
      const titleMatch = (row.title || '').toLowerCase().includes(term)
      const requesterMatch = formatRequesterWithDepartment(row.requester)
        .toLowerCase()
        .includes(term)
      const stageMatch = (row.stage?.name || '').toLowerCase().includes(term)
      return numberMatch || titleMatch || requesterMatch || stageMatch
    })
  }, [requests, search, requesterFilter])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
              Selecionar solicitação
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              Filtre e escolha a solicitação para vincular à nota fiscal.
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por número, título, solicitante ou etapa..."
              className="pl-9"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
              Solicitante
            </label>
            <select
              className={SELECT_CLASS + ' w-full'}
              value={requesterFilter}
              onChange={(e) => setRequesterFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {requesterOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              Nenhuma solicitação encontrada.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((row) => {
                const selected = pendingId === row.id
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setPendingId(row.id)}
                      className={`w-full px-3 py-3 text-left transition-colors ${
                        selected
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-900/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                            selected
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-gray-300 dark:border-gray-500'
                          }`}
                          aria-hidden
                        >
                          {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatRequestSelectLabel(row)}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            Etapa: {row.stage?.name || '—'}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onSelect('')
              onClose()
            }}
          >
            Limpar vínculo
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="solid"
              disabled={!pendingId}
              onClick={() => {
                if (!pendingId) return
                onSelect(pendingId)
                onClose()
              }}
            >
              Selecionar
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SolicitacaoSelectModal
